import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import axios from 'axios';
import {
  Users, Gamepad2, Video, Heart, Shield, Star,
  Zap, ChevronRight, Mail, ArrowRight, Smartphone, Monitor,
  Trophy, Gift, MessageCircle, Copy, Check, Share2
} from 'lucide-react';

import { API_BASE_URL } from '../config';
import { BRAND_ASSETS } from '../brandAssets';

const api = axios.create({ baseURL: `${API_BASE_URL}/api` });

// All navigation uses path-based routing under the same domain
// The web app's sign-in page (the app itself lives under /app)
const getAppUrl = () => '/beta-access';
// Round 44: the site leads with the mobile app. /get-app sends a phone or
// tablet straight to its store and shows both buttons on a computer.
const GET_APP_URL = '/get-app';
const getAdminUrl = () => '/admin';

const getLegalPath = (path) => {
  // Legal pages are on the landing domain
  return path;
};

const APP_URL = getAppUrl();

// Social share helpers
const DOMAIN = typeof window !== 'undefined' ? window.location.origin : 'https://gemslive.tv';

const getShareText = (refLink) => `Join me on GEMS — ${refLink}`;

const shareTwitter = (refLink) => {
  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText(refLink))}`, '_blank');
};
const shareWhatsApp = (refLink) => {
  window.open(`https://wa.me/?text=${encodeURIComponent(getShareText(refLink))}`, '_blank');
};
const shareInstagram = (refLink) => {
  // Instagram doesn't have a direct share URL; copy to clipboard for stories
  navigator.clipboard.writeText(getShareText(refLink));
  toast.success('Link copied! Paste it in your Instagram story or bio.');
};

const MarketingLandingPage = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlistData, setWaitlistData] = useState(null); // { waitlist_id, position, total, referral_count }
  const [copied, setCopied] = useState(false);
  // Round 40c: the download badges follow Admin → App Store Links (a
  // TestFlight / Play-testing link today, the store listings once live).
  const [appLinks, setAppLinks] = useState(null);
  useEffect(() => {
    api.get('/config/app-links').then((r) => setAppLinks(r.data || {})).catch(() => setAppLinks({}));
  }, []);
  const trackStoreClick = (storeType) => {
    try { api.post('/app-store/track-click', { store_type: storeType, page: 'landing-badges', referrer: document.referrer }).catch(() => {}); } catch (e) { /* noop */ }
  };

  // Read ref and UTM params from URL
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const refParam = urlParams.get('ref') || '';
  const utmSource = urlParams.get('utm_source') || '';
  const streamParam = urlParams.get('stream') || '';

  // Check if user already has a waitlist_id stored locally
  useEffect(() => {
    const storedId = localStorage.getItem('gems_waitlist_id');
    if (storedId) {
      fetchWaitlistStatus(storedId);
    }
  }, []);

  const fetchWaitlistStatus = async (wid) => {
    try {
      const res = await api.get(`/waitlist/status/${wid}`);
      setWaitlistData(res.data);
      setSubmitted(true);
    } catch (e) { /* not found, ignore */ }
  };

  const handleWaitlistSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email,
        source: 'landing_page',
        ...(refParam && { referred_by_user_id: refParam }),
        ...(utmSource && { utm_source: utmSource }),
        ...(streamParam && { stream_id: streamParam }),
      };
      const res = await api.post('/waitlist/signup', payload);
      const data = res.data;

      if (data.waitlist_id) {
        localStorage.setItem('gems_waitlist_id', data.waitlist_id);
        setWaitlistData({
          waitlist_id: data.waitlist_id,
          position: data.position,
          total: data.total,
          referral_count: data.referral_count || 0,
        });
      }

      setSubmitted(true);
      if (data.already_registered) {
        toast.info("You're already on the waitlist!");
      } else {
        toast.success("You're on the list! Share your link to move up.");
      }
      setEmail('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const referralLink = waitlistData ? `${DOMAIN}/?ref=${waitlistData.waitlist_id}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Periodically refresh position if submitted
  useEffect(() => {
    if (!waitlistData?.waitlist_id) return;
    const interval = setInterval(() => fetchWaitlistStatus(waitlistData.waitlist_id), 30000);
    return () => clearInterval(interval);
  }, [waitlistData?.waitlist_id]);

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white overflow-x-hidden" data-testid="marketing-landing-page">
      <Toaster position="top-right" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0e17]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img
                src={BRAND_ASSETS.logo}
                alt="GEMS"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
              <span className="text-lg sm:text-xl font-bold tracking-tight">
                GEMS
              </span>
            </div>
            <div className="flex items-center gap-3 sm:gap-4">
              <a href={APP_URL} className="hidden sm:inline text-sm text-gray-300 hover:text-white" data-testid="use-web-nav-link">
                Use on the web
              </a>
              <a
                href={GET_APP_URL}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm transition-all hover:shadow-lg hover:shadow-emerald-500/25"
                data-testid="enter-app-nav-cta"
              >
                Get the App
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-32 px-4">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-amber-500/8 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT: copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-8 text-sm text-gray-300">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                Live now
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
                Go Live.<br />
                Play Together.<br />
                <span className="bg-gradient-to-r from-emerald-400 via-amber-300 to-emerald-400 bg-clip-text text-transparent">
                  Have a Blast.
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl lg:max-w-none mx-auto lg:mx-0 mb-6 leading-relaxed">
                The interactive social entertainment platform where creators go live, share their world, and play face-to-face games like Spades, Crazy Eights, Checkers, Four-in-a-Row, and more with their audience — and where every viewer can leave the sidelines and get in the game.
              </p>

              <div className="max-w-2xl lg:max-w-none mx-auto lg:mx-0 mb-10 space-y-2">
                <p className="text-sm sm:text-base text-gray-300">
                  <span className="font-bold text-emerald-400">For Creators:</span> Stream, post, compete, and earn. Your audience is your team.
                </p>
                <p className="text-sm sm:text-base text-gray-300">
                  <span className="font-bold text-emerald-400">For Everyone Else:</span> Don't just watch. Jump in. Play face-to-face with creators and the whole room — live.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
                <a
                  href={APP_URL}
                  className="group flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-4 rounded-full text-base sm:text-lg transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5"
                  data-testid="enter-app-hero"
                >
                  <Smartphone className="w-5 h-5" />
                  Get the App
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              <p className="mt-6 text-sm text-gray-400" data-testid="hero-tagline">
                For the best experience, download the GEMS mobile app.{' '}
                <a href={APP_URL} className="text-emerald-400 hover:underline" data-testid="use-web-hero-link">Or use GEMS on the web</a>
              </p>
            </div>

            {/* RIGHT: real gameplay screenshot */}
            <div className="relative" data-testid="hero-gameplay-image">
              <p className="text-xs sm:text-sm text-emerald-400/80 font-medium tracking-wide text-center lg:text-left mb-3 uppercase">
                See real gameplay in action
              </p>
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_25px_70px_-20px_rgba(16,185,129,0.35)]">
                <img
                  src="/images/live-spades-gameplay.png"
                  alt="Live 4-player Spades gameplay on GEMS — real streamers, live cards, viewer count, full UI"
                  loading="eager"
                  className="w-full h-auto block"
                />
                {/* subtle gradient overlay for polish */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
              </div>
              {/* Two more real screenshots: duo broadcast + 4-In-A-Row */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4" data-testid="hero-more-screenshots">
                <div className="relative rounded-xl overflow-hidden ring-1 ring-white/10 shadow-[0_18px_50px_-24px_rgba(16,185,129,0.35)]">
                  <img src="/images/live-duo-broadcast.png" alt="Two creators streaming together live on GEMS" loading="lazy" className="w-full h-auto block" />
                </div>
                <div className="relative rounded-xl overflow-hidden ring-1 ring-white/10 shadow-[0_18px_50px_-24px_rgba(16,185,129,0.35)]">
                  <img src="/images/four-in-a-row-live.png" alt="Two players in a live 4-In-A-Row match on GEMS" loading="lazy" className="w-full h-auto block" />
                </div>
              </div>
              <p className="mt-4 text-sm sm:text-base text-gray-400 text-center lg:text-left leading-relaxed">
                Play face-to-face. Talk, compete, and stream — all in one place.
              </p>

              {/* decorative glows */}
              <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl" />
              <div className="pointer-events-none absolute -bottom-6 -left-6 w-28 h-28 bg-emerald-400/20 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Badges */}
      <section className="py-8 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center items-center gap-8 sm:gap-16">
          {appLinks?.ios ? (
            <a href={appLinks.ios} onClick={() => trackStoreClick('apple_store')} className="flex items-center gap-2 text-gray-200 hover:text-white" data-testid="badge-ios-download">
              <Smartphone className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-medium">iOS App — <span className="text-emerald-400">{/testflight/i.test(appLinks.ios) ? 'Download' : 'Download on the App Store'}</span></span>
            </a>
          ) : (
            <div className="flex items-center gap-2 text-gray-400">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm font-medium">iOS App — Coming Soon</span>
            </div>
          )}
          {appLinks?.android ? (
            <a href={appLinks.android} onClick={() => trackStoreClick('google_play')} className="flex items-center gap-2 text-gray-200 hover:text-white" data-testid="badge-android-download">
              <Smartphone className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-medium">Android App — <span className="text-emerald-400">{/play\.google/i.test(appLinks.android) ? 'Get it on Google Play' : 'Download'}</span></span>
            </a>
          ) : (
            <div className="flex items-center gap-2 text-gray-400">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm font-medium">Android App — Coming Soon</span>
            </div>
          )}
          <a href={APP_URL} className="flex items-center gap-2 text-gray-200 hover:text-white" data-testid="badge-web-app">
            <Monitor className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-medium">Web App — <span className="text-emerald-400">Live now</span></span>
          </a>
        </div>
      </section>

      {/* What is GEMS */}
      <section className="py-20 sm:py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-base sm:text-lg font-semibold text-emerald-400 mb-3">What is GEMS?</h2>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold max-w-3xl mx-auto leading-tight">
              An interactive social entertainment platform built for creators, players, and communities
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <FeatureCard
              icon={<Video className="w-6 h-6" />}
              title="Go Live Instantly"
              description="Start streaming with one tap. Share your gameplay, your vibe, and your personality with a growing audience."
              color="emerald"
            />
            <FeatureCard
              icon={<Gamepad2 className="w-6 h-6" />}
              title="Play Live"
              description="Spades, 4-In-A-Row, Checkers and more — real-time multiplayer with live video. Play with friends, family, or match with new opponents."
              color="amber"
            />
            <FeatureCard
              icon={<Users className="w-6 h-6" />}
              title="Build Your Community"
              description="Grow your follower base, connect with fans, and build meaningful relationships through shared experiences."
              color="blue"
            />
            <FeatureCard
              icon={<Gift className="w-6 h-6" />}
              title="Gift & Earn"
              description="Send and receive virtual gifts during live streams. Support your favorite creators and get recognized."
              color="rose"
            />
            <FeatureCard
              icon={<MessageCircle className="w-6 h-6" />}
              title="Real-Time Chat"
              description="Direct messaging, live chat during streams, and community interactions that keep you connected."
              color="violet"
            />
            <FeatureCard
              icon={<Trophy className="w-6 h-6" />}
              title="Compete & Win"
              description="Climb the leaderboards, earn streaks, and show off your skills across every game on GEMS."
              color="orange"
            />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 sm:py-32 px-4 bg-gradient-to-b from-transparent via-emerald-950/20 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-base sm:text-lg font-semibold text-emerald-400 mb-3">How It Works</h2>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold">Three steps to start streaming</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 sm:gap-12">
            <StepCard
              number="01"
              title="Create Your Profile"
              description="Sign up, customize your profile, and set your GEMS Tag. Your identity, your way."
            />
            <StepCard
              number="02"
              title="Go Live or Join a Game"
              description="Start a solo stream or jump into a live match — Spades, 4-In-A-Row, Checkers. Invite friends or match with new players."
            />
            <StepCard
              number="03"
              title="Grow & Monetize"
              description="Build your audience, receive gifts from fans, and turn your passion into a community experience."
            />
          </div>
        </div>
      </section>

      {/* For Creators / For Players */}
      <section className="py-20 sm:py-32 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 sm:gap-16">
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-3xl p-8 sm:p-10">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-6">
              <Star className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">For Creators</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              GEMS gives you the tools to build a real audience. Go live, play games, receive gifts, and grow your community — all in one place.
            </p>
            <ul className="space-y-3">
              {['One-tap live streaming', 'Virtual gifting & support', 'Follower & fan analytics', 'Creator verification badges'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-3 h-3 text-emerald-400" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-3xl p-8 sm:p-10">
            <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center mb-6">
              <Gamepad2 className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">For Players</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Find your people and play the way it was meant to be — live, social, and competitive. Every game is a shared experience.
            </p>
            <ul className="space-y-3">
              {['Real-time multiplayer games', 'Live video during gameplay', 'Matchmaking & invitations', 'Leaderboards & win streaks'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-300">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <ChevronRight className="w-3 h-3 text-amber-400" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-20 sm:py-32 px-4 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Shield className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Built with Safety First</h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            GEMS is powered by an AI workforce that monitors content, protects users, and ensures a safe environment for everyone. Our Trust & Safety systems run 24/7.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-5 h-5" />, label: 'AI Moderation' },
              { icon: <Zap className="w-5 h-5" />, label: 'Fraud Protection' },
              { icon: <Users className="w-5 h-5" />, label: 'Community Guidelines' },
              { icon: <Star className="w-5 h-5" />, label: 'Verified Badges' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="text-emerald-400">{item.icon}</div>
                <span className="text-sm text-gray-300 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-20 sm:py-32 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Get the GEMS App</h2>
          <p className="text-gray-400 text-base sm:text-lg mb-10">
            For the best experience, download the GEMS mobile app. Creating your account takes about a minute.
          </p>

          {submitted && waitlistData ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 space-y-6" data-testid="waitlist-success">
              {/* Position Card */}
              <div>
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-emerald-400 mb-1">You're on the list!</h3>
                <p className="text-4xl sm:text-5xl font-black text-white my-3" data-testid="waitlist-position">
                  #{waitlistData.position}
                </p>
                <p className="text-gray-400 text-sm">
                  out of {waitlistData.total.toLocaleString()} on the waitlist
                </p>
                {waitlistData.referral_count > 0 && (
                  <p className="text-emerald-400 text-sm mt-2" data-testid="waitlist-referral-count">
                    {waitlistData.referral_count} friend{waitlistData.referral_count !== 1 ? 's' : ''} referred
                  </p>
                )}
              </div>

              {/* Referral Link */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="waitlist-referral-section">
                <p className="text-sm text-gray-300 mb-3 font-medium">Invite friends to move up the list</p>
                <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg p-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate px-2"
                    data-testid="waitlist-referral-link"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-4 py-2 rounded-lg text-sm transition-all shrink-0"
                    data-testid="waitlist-copy-link"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Social Share Buttons */}
              <div className="space-y-3" data-testid="waitlist-share-buttons">
                <p className="text-sm text-gray-400">Share on</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => shareTwitter(referralLink)}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                    data-testid="share-twitter"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Twitter / X
                  </button>
                  <button
                    onClick={() => shareWhatsApp(referralLink)}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                    data-testid="share-whatsapp"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </button>
                  <button
                    onClick={() => shareInstagram(referralLink)}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                    data-testid="share-instagram"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    Instagram
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                    data-testid="share-copy"
                  >
                    <Share2 className="w-4 h-4" />
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Round 44: the site leads with the mobile app; the web app is a link away. */
            <a
              href={GET_APP_URL}
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-10 py-4 rounded-full text-lg transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5"
              data-testid="enter-app-waitlist-section"
            >
              <Smartphone className="w-5 h-5" />
              Get the App
              <ArrowRight className="w-5 h-5" />
            </a>
          )}
          {refParam && !submitted && (
            <p className="text-emerald-400/70 text-sm mt-4">Invited by a friend</p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-32 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-amber-500/10 border border-emerald-500/20 rounded-3xl p-10 sm:p-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Ready to Play?</h2>
          <p className="text-gray-400 text-base sm:text-lg mb-8 max-w-xl mx-auto">
            GEMS is live. Get the app, create your account, and start streaming, playing, and connecting today.
          </p>
          <a
            href={GET_APP_URL}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-10 py-4 rounded-full text-lg transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5"
            data-testid="enter-app-cta"
          >
            <Smartphone className="w-5 h-5" />
            Get the App
          </a>
          <p className="mt-5 text-sm text-gray-400">
            Prefer a browser? <a href={APP_URL} className="text-emerald-400 hover:underline" data-testid="use-web-cta-link">Use GEMS on the web</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={BRAND_ASSETS.logo}
                  alt="GEMS"
                  className="w-10 h-10 object-contain"
                />
                <span className="text-lg font-bold">
                  GEMS
                </span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                Game. Engage. Monetize. Stream.<br />
                The interactive social entertainment platform.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Product</h4>
              <ul className="space-y-3">
                <li><a href="/beta-access" className="text-gray-500 hover:text-white text-sm transition-colors">Web App</a></li>
                <li><a href="https://apps.apple.com/app/gems-live/id6736983498" className="text-gray-500 hover:text-white text-sm transition-colors">iOS App</a></li>
                <li><a href="https://play.google.com/store/apps/details?id=com.gemslive.app" className="text-gray-500 hover:text-white text-sm transition-colors">Android App</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Legal</h4>
              <ul className="space-y-3">
                <li><a href={getLegalPath('/terms')} className="text-gray-500 hover:text-white text-sm transition-colors">Terms of Service</a></li>
                <li><a href={getLegalPath('/privacy')} className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</a></li>
                <li><a href={getLegalPath('/community-guidelines')} className="text-gray-500 hover:text-white text-sm transition-colors">Community Guidelines</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">Support</h4>
              <ul className="space-y-3">
                <li><a href={getLegalPath('/support')} className="text-gray-500 hover:text-white text-sm transition-colors">Help Center</a></li>
                <li><a href="mailto:support@gemslive.tv" className="text-gray-500 hover:text-white text-sm transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">&copy; {new Date().getFullYear()} GEMS. All rights reserved.</p>
            
          </div>
        </div>
      </footer>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, color = 'emerald' }) => {
  const colors = {
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/10 text-emerald-400',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-500/10 text-amber-400',
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/10 text-blue-400',
    rose: 'from-rose-500/10 to-rose-500/5 border-rose-500/10 text-rose-400',
    violet: 'from-violet-500/10 to-violet-500/5 border-violet-500/10 text-violet-400',
    orange: 'from-orange-500/10 to-orange-500/5 border-orange-500/10 text-orange-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-2xl p-6 sm:p-8 hover:border-opacity-30 transition-all hover:-translate-y-1`}>
      <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-5 ${colors[color].split(' ').pop()}`}>
        {icon}
      </div>
      <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
};

// Step Card Component
const StepCard = ({ number, title, description }) => (
  <div className="text-center">
    <div className="text-5xl sm:text-6xl font-black text-emerald-500/20 mb-4">{number}</div>
    <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
  </div>
);

export default MarketingLandingPage;
